// DE-BRIEFS (founder-asked 2026-10-29: "so it feels like a company"): every DE that reports to a
// person sends that person a short brief ("Here's what I'm working on. Tell me if anything needs
// correcting."); the Chief of Staff sends the CEO one report; a reply is a correction. The rhythm
// is people's to set, never hard-coded: the company's defaults (daily, Monday to Friday, 08:00
// company time), a person's for the briefs they receive, a DE's as its manager sets it. A field
// left null inherits. Handlers: services/api/src/domain/briefs.
import { z } from "zod";
import {
  BriefChannel,
  BriefFrequency,
  BriefRuleRecipient,
  BriefScope,
  BriefSection,
  DeBriefKind,
  DeBriefState,
} from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const agents = { feature: "agents" } as const;

const Clock = z
  .string()
  .max(5)
  .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, "HH:MM, 24-hour");
const Weekday = z.int().min(1).max(7);

/** The rhythm, as stored at one level: each field null inherits from the level above. */
export const BriefSettingFields = z
  .strictObject({
    enabled: z.boolean().nullable(),
    frequency: BriefFrequency.nullable(),
    /** ISO weekdays (1 Monday … 7 Sunday): `weekly` takes its first, `custom` all of them. */
    days: z.array(Weekday).min(1).max(7).nullable(),
    /** Company-local time, `HH:MM`. */
    time: Clock.nullable(),
  })
  .meta({ id: "BriefSettingFields" });
export type BriefSettingFields = z.infer<typeof BriefSettingFields>;

/** The rhythm as it applies (every field resolved). */
export const BriefSchedule = z
  .strictObject({
    enabled: z.boolean(),
    frequency: BriefFrequency,
    days: z.array(Weekday).min(1).max(7),
    time: Clock,
  })
  .meta({ id: "BriefSchedule" });
export type BriefSchedule = z.infer<typeof BriefSchedule>;

const Person = z.object({ membership_id: Uuid, name: z.string() }).meta({ id: "BriefPerson" });

export const BriefSettingsView = z
  .object({
    scope: BriefScope,
    /** The person or the DE; null for the company. */
    subject_id: Uuid.nullable(),
    /** What this level stores (null fields inherit). */
    setting: BriefSettingFields,
    /** What the level above gives (the company's defaults, or the company's over the person's). */
    inherited: BriefSchedule,
    /** What applies now. */
    effective: BriefSchedule,
    /** The company's zone (times are its clock). */
    time_zone: z.string(),
    /** The next brief at this rhythm; null when off. */
    next_at: Timestamp.nullable(),
    /** A DE: who gets its brief (its recipients, or the person its seat reports to). */
    recipients: z.array(Person),
    /** A DE: whether `recipients` is the manager's choice (else the reporting line's). */
    recipients_set: z.boolean(),
    /** A DE: the people who may receive it (the company's active people). */
    recipient_choices: z.array(Person),
    /**
     * A DE: each recipient's rhythm as it applies to them (the company's, their own, the DE's)
     * and their next brief. `next_at` above is the soonest of these.
     */
    recipient_schedules: z.array(
      z.object({ person: Person, effective: BriefSchedule, next_at: Timestamp.nullable() }),
    ),
    /** The company: the Chief of Staff DE (the CEO report's sender) and the CEO it goes to. */
    cos: z
      .object({
        membership_id: Uuid.nullable(),
        set: z.boolean(),
        ceo: Person.nullable(),
        choices: z.array(Person),
      })
      .nullable(),
    can_edit: z.boolean(),
  })
  .meta({ id: "BriefSettingsView" });
export type BriefSettingsView = z.infer<typeof BriefSettingsView>;

export const BriefSettingsPut = z
  .strictObject({
    ...BriefSettingFields.shape,
    /** A DE only: its recipients (people); null: the person its seat reports to. */
    recipients: z.array(Uuid).max(20).nullable().optional(),
    /** The company only: the Chief of Staff DE; null: the DE with the digest on. */
    cos_membership_id: Uuid.nullable().optional(),
  })
  .meta({ id: "BriefSettingsPut" });
export type BriefSettingsPut = z.infer<typeof BriefSettingsPut>;

/** One line of a brief, numbered as the brief lists it (a reply may name it: "2: …"). */
export const BriefItemView = z
  .object({
    n: z.int(),
    kind: z.enum(["task", "run", "kpi", "blocker", "ask"]),
    id: Uuid,
    title: z.string(),
    note: z.string(),
    task_id: Uuid.nullable(),
  })
  .meta({ id: "BriefItem" });

/** A brief as its DE reads it (`get_brief`): the facts to put in its own words. */
export const AgentBrief = z
  .object({
    id: Uuid,
    kind: DeBriefKind,
    state: DeBriefState,
    person: Person,
    as_of: Timestamp,
    items: z.array(BriefItemView),
    team: z
      .array(
        z.object({
          de_membership_id: Uuid,
          name: z.string(),
          doing: z.int(),
          blocked: z.int(),
          asks: z.int(),
          kpis_off: z.int(),
        }),
      )
      .nullable(),
    /** The structured summary that goes if the DE sends nothing by `draft_until`. */
    summary_markdown: z.string(),
    draft_until: Timestamp.nullable(),
    message_id: Uuid.nullable(),
  })
  .meta({ id: "AgentBrief" });
export type AgentBrief = z.infer<typeof AgentBrief>;

export const AgentBriefSend = z
  .strictObject({
    /** The brief in the DE's words (the numbered lines keep their numbers). */
    body_markdown: z.string().min(1).max(8000),
  })
  .meta({ id: "AgentBriefSend" });

const DeParams = z.object({ co: Uuid, id: Uuid.describe("The DE's membership id") });

/** COS-BRIEF-RULES: a brief or report as its recipient reads it (the inbox item's full page). */
export const BriefReport = z
  .object({
    id: Uuid,
    kind: DeBriefKind,
    from: Person,
    as_of: Timestamp,
    delivered_at: Timestamp.nullable(),
    channel: BriefChannel,
    /** The sections a rule chose; null: the whole report. */
    sections: z.array(BriefSection).nullable(),
    for_owner: z.boolean(),
    items: z.array(BriefItemView),
    team: z
      .array(
        z.object({
          de_membership_id: Uuid,
          name: z.string(),
          doing: z.int(),
          blocked: z.int(),
          asks: z.int(),
          kpis_off: z.int(),
        }),
      )
      .nullable(),
    /** "and N more" per kind, when a list was cut short. */
    more: z.record(z.string(), z.int()),
    /** The week's numbers (a CoS report to an owner or admin); null: none. */
    numbers: z.array(z.string()).nullable(),
    /** Memory contradictions waiting for the reader, with the review's link; null: none. */
    memory_conflicts: z.object({ open: z.int().min(1), url: z.string() }).nullable(),
    /** The plain report (what the email said). */
    markdown: z.string(),
  })
  .meta({ id: "BriefReport" });
export type BriefReport = z.infer<typeof BriefReport>;

// COS-BRIEF-RULES (founder-asked 2026-10-30) ------------------------------------------------------
// The CoS report always goes to the CEO. A rule sends it (some sections of it, on its own rhythm
// and channel) to someone else — the company owner when the owner is not the CEO — or tunes the
// CEO's own. Rules are set only on the CEO's word: the CoS's tools name the CEO's message (the
// server checks its author), or the CEO (or an admin) sets them in the settings.

/** Who a rule is for: the owner (a role, resolved when it is sent) or a person. */
export const BriefRuleRecipientRef = z
  .discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("owner") }),
    z.strictObject({ kind: z.literal("person"), membership_id: Uuid }),
  ])
  .meta({ id: "BriefRuleRecipientRef" });

export const BriefRuleView = z
  .object({
    id: Uuid,
    recipient: z.object({
      kind: BriefRuleRecipient,
      /** The person it goes to now (the owner resolved); null: no one holds the role. */
      membership_id: Uuid.nullable(),
      name: z.string().nullable(),
    }),
    sections: z.array(BriefSection).min(1),
    enabled: z.boolean(),
    frequency: BriefFrequency,
    days: z.array(Weekday).nullable(),
    time: Clock,
    channel: BriefChannel,
    /** Whether it sends now (an owner rule does not while the owner is the CEO). */
    applies: z.boolean(),
    /** Why it does not apply, in words; null when it does. */
    note: z.string().nullable(),
    next_at: Timestamp.nullable(),
    /** The CEO's message the CoS acted on; null: set in the settings. */
    instruction_message_id: Uuid.nullable(),
    updated_at: Timestamp,
  })
  .meta({ id: "BriefRuleView" });
export type BriefRuleView = z.infer<typeof BriefRuleView>;

export const BriefRulesView = z
  .object({
    rules: z.array(BriefRuleView),
    ceo: Person.nullable(),
    owner: Person.nullable(),
    /** The owner is the CEO: the CEO report already reaches them (no owner rule applies). */
    owner_is_ceo: z.boolean(),
    cos: Person.nullable(),
    time_zone: z.string(),
    /** The CEO, or an admin. */
    can_edit: z.boolean(),
    /** The company's active people (who a person rule may name). */
    recipient_choices: z.array(Person),
  })
  .meta({ id: "BriefRulesView" });
export type BriefRulesView = z.infer<typeof BriefRulesView>;

/**
 * Set (create or change) the rule of one recipient. Left out: the stored value stays; for a new
 * rule, every section, the owner weekly (Friday) and a person on their current rhythm, the DM.
 */
export const BriefRulePut = z
  .strictObject({
    recipient: BriefRuleRecipientRef,
    sections: z.array(BriefSection).min(1).max(4).optional(),
    enabled: z.boolean().optional(),
    frequency: BriefFrequency.optional(),
    days: z.array(Weekday).min(1).max(7).nullable().optional(),
    time: Clock.optional(),
    channel: BriefChannel.optional(),
  })
  .meta({ id: "BriefRulePut" });
export type BriefRulePut = z.infer<typeof BriefRulePut>;

const Instruction = Uuid.describe(
  "The CEO's chat message asking for this change (its author must be the CEO or an admin)",
);

export const AgentBriefRuleSet = z
  .strictObject({ ...BriefRulePut.shape, instruction_message_id: Instruction })
  .meta({ id: "AgentBriefRuleSet" });

export const AgentBriefRuleRemove = z
  .strictObject({
    instruction_message_id: Instruction,
    recipient: BriefRuleRecipientRef,
    /** The owner: also stop asking the CEO what the owner should get. */
    stop_asking: z.boolean().optional(),
  })
  .meta({ id: "AgentBriefRuleRemove" });

export const AgentBriefRuleChange = z
  .object({
    view: BriefRulesView,
    /** What changed, in the words the CoS confirmed to the CEO with. */
    done: z.string(),
    /** The confirmation posted in the CEO's thread. */
    confirmation_message_id: Uuid.nullable(),
  })
  .meta({ id: "AgentBriefRuleChange" });

export const deBriefRoutes = {
  "briefSettings.getCompany": defineRoute({
    ...agents,
    id: "briefSettings.getCompany",
    method: "get",
    path: `${base}/brief-settings`,
    tag: "briefs",
    summary: "The company's brief defaults and its Chief of Staff report (managers edit)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: BriefSettingsView },
    errors: [],
    emits: [],
  }),
  "briefSettings.putCompany": defineRoute({
    ...agents,
    id: "briefSettings.putCompany",
    method: "put",
    path: `${base}/brief-settings`,
    tag: "briefs",
    summary: "Set the company's brief defaults: on or off, frequency, days, time, the CoS",
    access: "manager",
    params: CompanyParams,
    body: BriefSettingsPut,
    response: { status: 200, schema: BriefSettingsView },
    errors: ["validation_failed"],
    emits: ["brief_settings.updated"],
  }),
  "briefSettings.getMine": defineRoute({
    ...agents,
    id: "briefSettings.getMine",
    method: "get",
    path: `${base}/brief-settings/me`,
    tag: "briefs",
    summary: "The caller's own rhythm for the briefs they receive",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: BriefSettingsView },
    errors: [],
    emits: [],
  }),
  "briefSettings.putMine": defineRoute({
    ...agents,
    id: "briefSettings.putMine",
    method: "put",
    path: `${base}/brief-settings/me`,
    tag: "briefs",
    summary: "Set the caller's rhythm for the briefs they receive (null fields inherit)",
    access: "member",
    params: CompanyParams,
    body: BriefSettingsPut,
    response: { status: 200, schema: BriefSettingsView },
    errors: ["validation_failed"],
    emits: ["brief_settings.updated"],
  }),
  "briefSettings.getDe": defineRoute({
    ...agents,
    id: "briefSettings.getDe",
    method: "get",
    path: `${base}/digital-employees/{id}/brief-settings`,
    tag: "briefs",
    summary: "A DE's brief: its rhythm and recipients (its manager edits)",
    access: "member",
    params: DeParams,
    response: { status: 200, schema: BriefSettingsView },
    errors: [],
    emits: [],
  }),
  "briefSettings.putDe": defineRoute({
    ...agents,
    id: "briefSettings.putDe",
    method: "put",
    path: `${base}/digital-employees/{id}/brief-settings`,
    tag: "briefs",
    summary:
      "Set a DE's brief rhythm and recipients (a person up its reporting line, or a manager)",
    access: "member",
    params: DeParams,
    body: BriefSettingsPut,
    response: { status: 200, schema: BriefSettingsView },
    errors: ["validation_failed"],
    emits: ["brief_settings.updated"],
  }),
  "agentBriefs.get": defineRoute({
    ...agents,
    id: "agentBriefs.get",
    method: "get",
    path: `${base}/agent/briefs/{id}`,
    tag: "briefs",
    summary: "get_brief: the facts of a brief the DE was woken to write (its own only)",
    access: "agent_key",
    params: CompanyItemParams,
    response: { status: 200, schema: AgentBrief },
    errors: [],
    emits: [],
  }),
  "agentBriefs.send": defineRoute({
    ...agents,
    id: "agentBriefs.send",
    method: "post",
    path: `${base}/agent/briefs/{id}/send`,
    tag: "briefs",
    summary: "send_brief: send the brief in the DE's words, as a DM, under the start_thread rules",
    description:
      "DE-BRIEFS. The DE's agent key (or its run). One brief per person a day; quiet hours queue " +
      "it; a DE that is not active sends nothing (409). A brief already sent or past its draft " +
      "window (the summary went) answers 409.",
    access: "agent_key",
    params: CompanyItemParams,
    body: AgentBriefSend,
    response: { status: 200, schema: AgentBrief },
    errors: ["conflict"],
    emits: ["message.created", "topic.created", "channel.created", "approval.requested"],
  }),
  // COS-BRIEF-RULES
  "briefs.get": defineRoute({
    ...agents,
    id: "briefs.get",
    method: "get",
    path: `${base}/briefs/{id}`,
    tag: "briefs",
    summary: "A brief or report sent to the caller, in full (an inbox item's page)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BriefReport },
    errors: [],
    emits: [],
  }),
  "briefRules.list": defineRoute({
    ...agents,
    id: "briefRules.list",
    method: "get",
    path: `${base}/brief-rules`,
    tag: "briefs",
    summary: "Who else gets the Chief of Staff report, which sections, how often and how",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: BriefRulesView },
    errors: [],
    emits: [],
  }),
  "briefRules.put": defineRoute({
    ...agents,
    id: "briefRules.put",
    method: "put",
    path: `${base}/brief-rules`,
    tag: "briefs",
    summary: "Set the rule of one recipient (the CEO, or an admin)",
    access: "member",
    params: CompanyParams,
    body: BriefRulePut,
    response: { status: 200, schema: BriefRulesView },
    errors: ["validation_failed"],
    emits: ["brief_rule.updated"],
  }),
  "briefRules.delete": defineRoute({
    ...agents,
    id: "briefRules.delete",
    method: "delete",
    path: `${base}/brief-rules/{id}`,
    tag: "briefs",
    summary: "Remove a brief rule (the CEO, or an admin)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: BriefRulesView },
    errors: [],
    emits: ["brief_rule.updated"],
  }),
  "agentBriefRules.list": defineRoute({
    ...agents,
    id: "agentBriefRules.list",
    method: "get",
    path: `${base}/agent/brief-rules`,
    tag: "briefs",
    summary: "list_brief_rules: the brief rules (the Chief of Staff only)",
    access: "agent_key",
    params: CompanyParams,
    response: { status: 200, schema: BriefRulesView },
    errors: [],
    emits: [],
  }),
  "agentBriefRules.set": defineRoute({
    ...agents,
    id: "agentBriefRules.set",
    method: "put",
    path: `${base}/agent/brief-rules`,
    tag: "briefs",
    summary: "set_brief_rule: set a rule on the CEO's instruction (the Chief of Staff only)",
    description:
      "COS-BRIEF-RULES. `instruction_message_id` names the CEO's message: its author must be the " +
      "CEO (the head seat's holder) or an admin, in a conversation the CoS is in, within 7 days. " +
      "The change is audited and confirmed in that thread.",
    access: "agent_key",
    params: CompanyParams,
    body: AgentBriefRuleSet,
    response: { status: 200, schema: AgentBriefRuleChange },
    errors: ["validation_failed", "conflict"],
    emits: ["brief_rule.updated", "message.created"],
  }),
  "agentBriefRules.remove": defineRoute({
    ...agents,
    id: "agentBriefRules.remove",
    method: "post",
    path: `${base}/agent/brief-rules/remove`,
    tag: "briefs",
    summary: "remove_brief_rule: remove a rule (or stop asking about the owner) on the CEO's word",
    access: "agent_key",
    params: CompanyParams,
    body: AgentBriefRuleRemove,
    response: { status: 200, schema: AgentBriefRuleChange },
    errors: ["validation_failed", "conflict"],
    emits: ["brief_rule.updated", "message.created"],
  }),
} as const;
